import { useLayoutEffect, useState } from "react";
import type { RefObject } from "react";

/**
 * The live width of an element: what a list page measures to decide whether an
 * open item fits beside it as a side panel (`resolveSidePanelLayout`). Starts at
 * the window's width, so the first paint frames sensibly before the element is
 * measured.
 *
 * The element must be mounted on the first render — the ref is read once.
 */
export function useElementWidth(ref: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(() =>
    typeof window === "undefined" ? 0 : window.innerWidth,
  );
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}
